import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { buildDirectoryReminderUrl } from "@/lib/whatsapp";
import { buildRolePathChips } from "@/lib/rolePaths";
import PhotoDownloadDialog from "@/components/PhotoDownloadDialog";
import PhotoBrowserDialog from "@/components/PhotoBrowserDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Users, LogOut, ChevronLeft, Plus, Trash2, Pencil, Layers, Send, Settings, Upload,
    Search, ChevronRight, MessageSquare, CheckCircle2, Clock, LayoutGrid, List as ListIcon,
    ChevronDown, BadgeCheck, ShieldCheck, Download, FileSpreadsheet, RefreshCw, Wand2, ImagePlus, FileText, Sparkles, Cloud, Loader2,
} from "lucide-react";
import { List as VList } from "react-window";
import MemberProfileDialog from "./MemberProfileDialog";
import { AssignRoleDialog } from "./AssignRoleDialog";
import { WhatsAppComposeDialog } from "@/components/WhatsAppComposeDialog";

function fmtErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
    return d?.msg || String(d);
}

export default function AdminYearView() {
    const { yid } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("members");
    const [members, setMembers] = useState([]);
    const [committees, setCommittees] = useState([]);
    const [context, setContext] = useState(null);
    const [remindingAll, setRemindingAll] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [normalizing, setNormalizing] = useState(false);
    const [driveSyncing, setDriveSyncing] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [dedupingSheet, setDedupingSheet] = useState(false);
    const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
    const [photoBrowserOpen, setPhotoBrowserOpen] = useState(false);
    // Iter 100k · Bulk import + consent audit state
    const [importingRegistry, setImportingRegistry] = useState(false);
    const [consentAudit, setConsentAudit] = useState(null);
    const [consentDialogOpen, setConsentDialogOpen] = useState(false);
    // Iter 100n · Bulk consent grant + sheet-header fix
    const [grantingConsent, setGrantingConsent] = useState(false);
    const [fixingHeaders, setFixingHeaders] = useState(false);

    const doDedupeSheet = async () => {
        if (!isSuper) return;
        if (!window.confirm(
            "Clean Sheet?\n\n" +
            "This will:\n" +
            "  • Deduplicate the Member Registry — keep the most complete row per phone\n" +
            "  • Merge duplicate rows in this year tab (same phone + position)\n" +
            "  • Delete blank rows (physically, so the sheet shrinks)\n" +
            "  • Delete test-data rows (TEST_*, Sheet Sync Test, etc.)\n" +
            "  • Remove all Protected ranges\n" +
            "  • Unhide any hidden rows\n\n" +
            "Idempotent — safe to run multiple times."
        )) return;
        setDedupingSheet(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/dedupe-sheet`);
            toast.success("Sheet cleaned", {
                description:
                    `${data.registry_duplicates_merged || 0} registry dupes merged · ` +
                    `${data.duplicates_removed || 0} year dupes merged · ` +
                    `${data.blank_rows_deleted || 0} blank rows deleted · ` +
                    `${data.test_rows_deleted || 0} test rows deleted · ` +
                    `${data.protected_ranges_removed || 0} protections removed`,
                duration: 10000,
            });
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Cleanup failed");
        } finally {
            setDedupingSheet(false);
        }
    };
    const importFileRef = useRef(null);
    const bulkPhotoRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [assignRoleOpen, setAssignRoleOpen] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [profileMember, setProfileMember] = useState(null);
    const [profileMode, setProfileMode] = useState("view");
    const [newCommittee, setNewCommittee] = useState("");
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [subTab, setSubTab] = useState("unfilled");
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === "undefined") return "grid";
        return window.localStorage.getItem("yearview:mode") || "grid";
    });
    useEffect(() => {
        if (typeof window !== "undefined") window.localStorage.setItem("yearview:mode", viewMode);
    }, [viewMode]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, c, ctx] = await Promise.all([
                api.get(`/scoped/district-years/${yid}/members`),
                api.get(`/scoped/district-years/${yid}/committees`),
                api.get(`/scoped/district-years/${yid}/context`),
            ]);
            setMembers(m.data);
            setCommittees(c.data);
            setContext(ctx.data);
        } catch (e) {
            if (e?.response?.status === 403) toast.error("You don't have access to this year");
            else toast.error(fmtErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [yid]);
    useEffect(() => { if (yid) load(); }, [yid, load]);

    // Iter 100k · Consent audit fetcher. Cheap read, refreshed on
    // load and when the admin re-opens the widget.
    const loadConsentAudit = useCallback(async () => {
        if (!yid) return;
        try {
            const { data } = await api.get(`/scoped/district-years/${yid}/consent-audit`);
            setConsentAudit(data);
        } catch {
            // Non-blocking — widget stays hidden if the fetch fails.
        }
    }, [yid]);
    useEffect(() => { if (yid) loadConsentAudit(); }, [yid, loadConsentAudit]);

    // Iter 100k · Warm-sync. Silently pulls the district's shared
    // Member Registry into Mongo the first time an admin lands on
    // this year page. Fire-and-forget — refreshes the members list
    // when the background pull finishes so newly-hydrated Registry
    // rows appear without a manual reload. Coalesced server-side.
    useEffect(() => {
        if (!yid) return;
        let cancelled = false;
        (async () => {
            try {
                const start = await api.post(`/scoped/district-years/${yid}/warm-sync`);
                if (start?.data?.already_running) return;
                const started = Date.now();
                while (!cancelled && Date.now() - started < 5 * 60 * 1000) {
                    await new Promise(r => setTimeout(r, 5000));
                    if (cancelled) return;
                    const { data: st } = await api.get(`/scoped/district-years/${yid}/warm-sync/status`);
                    if (st?.state === "done") {
                        // Only quietly refresh if the pull actually changed
                        // anything — avoids a needless re-render.
                        if ((st.inserted || 0) + (st.updated || 0) > 0) load();
                        loadConsentAudit();
                        break;
                    }
                    if (st?.state === "error" || st?.state === "idle") break;
                }
            } catch {
                // Silent — warm-sync is best-effort, never surface to the admin.
            }
        })();
        return () => { cancelled = true; };
    }, [yid, load, loadConsentAudit]);

    const doGrantConsentAll = async () => {
        if (!canWrite) return;
        if (!window.confirm(
            "Grant consent to every member on this year's roster who hasn't accepted yet?\n\n" +
            "This stamps their consent_accepted_at regardless of profile completion status. Idempotent — already-accepted rows are untouched."
        )) return;
        setGrantingConsent(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/grant-consent-all`);
            toast.success(`Consent granted to ${data?.granted || 0} member${(data?.granted || 0) === 1 ? "" : "s"}`);
            loadConsentAudit();
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Could not grant consent");
        } finally {
            setGrantingConsent(false);
        }
    };

    const doFixSheetHeaders = async () => {
        if (!canWrite) return;
        setFixingHeaders(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/fix-sheet-headers`);
            toast.success("Sheet headers widened", {
                description: "Year tab + Member Registry now include Spouse Phone / Spouse Email columns.",
                duration: 6000,
            });
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Header fix failed");
        } finally {
            setFixingHeaders(false);
        }
    };

    const doImportRegistry = async () => {        if (!canWrite) return;
        if (!window.confirm(
            "Bulk-import every row from the Member Registry sheet into this district?\n\n" +
            "New members are added, existing ones are updated field-by-field (blank cells never wipe your database)."
        )) return;
        setImportingRegistry(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/import-registry`);
            if (data?.ok) {
                toast.success("Registry imported", {
                    description: `${data.total} rows read · +${data.inserted} new · ${data.updated} updated${data.skipped ? ` · ${data.skipped} skipped` : ""}`,
                    duration: 8000,
                });
                load();
                loadConsentAudit();
            } else {
                toast.error(`Import failed: ${data?.reason || "unknown error"}`);
            }
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Bulk import failed");
        } finally {
            setImportingRegistry(false);
        }
    };

    // Iter 87 — deep-link support. When the URL carries `?open=<yrid>`
    // (e.g. clicked from a Google Sheet "App Edit Link" cell), locate
    // the matching member in the loaded list and auto-open their profile
    // dialog in EDIT mode so admins land straight on the editor. The
    // param is consumed once and cleaned out of the URL so back-nav
    // doesn't re-open the dialog.
    useEffect(() => {
        const targetYrid = searchParams.get("open");
        if (!targetYrid || loading || !members?.length) return;
        const match = members.find((m) => String(m?.year_role?.id) === String(targetYrid));
        if (match) {
            setProfileMember(match);
            setProfileMode("edit");
            const next = new URLSearchParams(searchParams);
            next.delete("open");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, loading, members, setSearchParams]);

    const commMap = useMemo(() => Object.fromEntries(committees.map((c) => [c.id, c])), [committees]);

    const lifecycle = context?.lifecycle || "active";
    const isSuper = user?.role === "super_admin";
    const isReadonly = lifecycle === "readonly" && !isSuper;
    const isDisabled = lifecycle === "disabled" && !isSuper;
    const canWrite = !isReadonly && !isDisabled;

    const doLogout = async () => { await logout(); navigate("/admin/login"); };

    const remindAll = async () => {
        if (!canWrite) return;
        const pending = members.filter((m) => (m.year_role.progress_pct || 0) < 100).length;
        if (pending === 0) {
            toast.info("No unfilled members to remind");
            return;
        }
        if (!window.confirm(`Send a reminder to all ${pending} unfilled members?`)) return;
        setRemindingAll(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/remind-all-unfilled`);
            toast.success(`Sent ${data.sent}/${data.total} reminders`, {
                description: data.failed > 0 ? `${data.failed} failed` : undefined,
            });
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
        finally { setRemindingAll(false); }
    };

    const askAllToVerify = async () => {
        if (!canWrite) return;
        const total = members.length;
        if (total === 0) { toast.info("No members to notify"); return; }
        if (!window.confirm(`Ask all ${total} members to review and verify their entry?`)) return;
        setRemindingAll(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/remind-all-verify`);
            toast.success(`Verification request sent to ${data.sent}/${data.total} members`, {
                description: data.failed > 0 ? `${data.failed} failed` : undefined,
            });
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
        finally { setRemindingAll(false); }
    };

    const toggleVerified = async (m) => {
        if (!canWrite || !m?.year_role?.id) return;
        const yrid = m.year_role.id;
        const isVerified = !!m.year_role.verified_at;
        try {
            const { data } = isVerified
                ? await api.delete(`/scoped/year-roles/${yrid}/verify`)
                : await api.post(`/scoped/year-roles/${yrid}/verify`);
            setMembers((rows) => rows.map((r) => (r.year_role.id === yrid ? { ...r, ...data } : r)));
            setProfileMember((prev) => (prev && prev.year_role.id === yrid ? { ...prev, ...data } : prev));
            toast.success(isVerified ? "Verification removed" : "Marked verified");
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    // ---- Toolbar actions ----
    const [exportCommittee, setExportCommittee] = useState("");
    const [exportCompletion, setExportCompletion] = useState("");
    // Iter 115 — the export mirrors the screen: the Status dropdown
    // follows the active All/Filled/Unfilled/Verified tab (you can
    // still override it), and doExport also passes the search text.
    useEffect(() => {
        setExportCompletion(subTab === "all" ? "" : subTab);
    }, [subTab]);
    const doExport = async () => {
        try {
            const params = {};
            if (exportCommittee) params.committee = exportCommittee;
            if (exportCompletion) params.completion = exportCompletion;
            // Iter 115 — the export mirrors the screen: the search box
            // text narrows the member set exactly like the grid does.
            if (q.trim()) params.search = q.trim();
            const res = await api.get(`/scoped/district-years/${yid}/export`, { responseType: "blob", params });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/.exec(cd);
            a.download = match ? match[1] : `members-${yid}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Roster downloaded");
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Export failed"); }
    };

    const doPhotosZip = async () => {
        // Iter 100u · async ZIP builder. POST /photos/zip/start kicks
        // off a background task that writes the archive to /tmp on
        // the pod; we poll every 3s for status and surface a toast
        // with a Download link the moment the file is ready. Zero
        // frontend memory pressure regardless of roster size.
        const params = {};
        if (exportCommittee) params.committee = exportCommittee;
        if (exportCompletion) params.completion = exportCompletion;
        try {
            const { data: start } = await api.post(`/scoped/district-years/${yid}/photos/zip/start`, null, { params });
            const jobId = start?.job_id;
            if (!jobId) { toast.error("Zip build failed to start"); return; }
            toast.info("Zipping started · we'll ping you when it's ready", { description: start.filename });
            const started = Date.now();
            let lastProgress = -1;
            while (Date.now() - started < 15 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 3000));
                const { data: st } = await api.get(`/scoped/district-years/${yid}/photos/zip/status/${jobId}`);
                if (st?.state === "ready") {
                    const dl = `${api.defaults.baseURL}/scoped/district-years/${yid}/photos/zip/download/${jobId}`;
                    // Auto-trigger download using a hidden anchor so the
                    // browser respects cookies and Content-Disposition.
                    const a = document.createElement("a");
                    a.href = dl; a.download = st.filename || "photos.zip";
                    document.body.appendChild(a); a.click(); a.remove();
                    toast.success(`Download Ready — ${st.progress} photos`, {
                        description: st.filename,
                        action: { label: "Download again", onClick: () => window.open(dl, "_blank") },
                        duration: 15000,
                    });
                    return;
                }
                if (st?.state === "error") {
                    toast.error(`Zip failed: ${st.error || "unknown error"}`);
                    return;
                }
                if (typeof st?.progress === "number" && st.progress !== lastProgress) {
                    lastProgress = st.progress;
                }
            }
            toast.warning("Still zipping after 15 min — check back shortly");
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Photo download failed");
        }
    };

    const doSyncSheet = async () => {
        if (!canWrite) return;
        if (!window.confirm("Pull the latest rows from the linked data source and merge them into the database?")) return;
        setSyncing(true);
        try {
            const start = await api.post(`/scoped/district-years/${yid}/sync-sheet`);
            if (start?.data?.already_running) {
                toast.info("Sync already in progress — waiting for it to finish…");
            } else {
                toast.info("Sync started — this may take up to a couple of minutes for large sheets.");
            }
            // Poll for completion. Sync runs in background to avoid the
            // Cloudflare 100s edge timeout on long Atlas/Sheets round-trips.
            const started = Date.now();
            while (Date.now() - started < 15 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 4000));
                const { data: st } = await api.get(`/scoped/district-years/${yid}/sync-sheet/status`);
                if (st?.state === "done") {
                    toast.success("Database sync complete", {
                        description: `Read ${st.read || 0} · +${st.inserted_persons || 0} new · ${st.updated_persons || 0} updated · ${st.new_year_roles || 0} added to year${st.year_roles_deleted ? ` · ${st.year_roles_deleted} removed` : ""}`,
                    });
                    load();
                    break;
                }
                if (st?.state === "error") {
                    toast.error(`Sync failed: ${st.error || "unknown error"}`);
                    break;
                }
            }
        } catch (e) {
            const msg = fmtErr(e?.response?.data?.detail) || "Sync failed";
            const notConfigured = msg.toLowerCase().includes("no linked data source");
            toast.error(msg, notConfigured ? {
                description: "Open Settings and paste the Google Sheet URL for this year, then Sync again.",
                action: { label: "Open Settings", onClick: () => setTab("settings") },
                duration: 8000,
            } : undefined);
        }
        finally { setSyncing(false); }
    };

    const doImport = async (file) => {
        if (!file || !canWrite) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/scoped/district-years/${yid}/import`, fd);
            toast.success("Import complete", {
                description: `+${data.inserted_persons} new · ${data.updated_persons} updated · ${data.new_year_roles} added to year${data.skipped_rows ? ` · ${data.skipped_rows} skipped` : ""}`,
            });
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Import failed"); }
        finally { setImporting(false); if (importFileRef.current) importFileRef.current.value = ""; }
    };

    const doBulkPhotos = async (files) => {
        if (!files?.length || !canWrite) return;
        setBulkUploading(true);
        try {
            const fd = new FormData();
            Array.from(files).forEach((f) => fd.append("files", f));
            const { data } = await api.post(`/scoped/district-years/${yid}/photos/bulk-upload`, fd);
            toast.success(`Uploaded ${data.uploaded} photo${data.uploaded === 1 ? "" : "s"}`, {
                description: data.errors?.length ? `${data.errors.length} error(s)` : undefined,
            });
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Upload failed"); }
        finally { setBulkUploading(false); if (bulkPhotoRef.current) bulkPhotoRef.current.value = ""; }
    };

    const doNormalize = async () => {
        if (!canWrite) return;
        if (!window.confirm("Rename all photos for this year to the Name_Phone convention?")) return;
        setNormalizing(true);
        try {
            await api.post(`/scoped/district-years/${yid}/photos/normalize`);
            toast.info("Normalize started — this can take a couple of minutes for large years.");
            // Poll for completion
            const started = Date.now();
            while (Date.now() - started < 5 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 4000));
                const { data: st } = await api.get(`/scoped/district-years/${yid}/photos/normalize/status`);
                if (st?.state === "done") {
                    toast.success("Normalize complete", {
                        description: `${st.renamed} renamed · ${st.skipped} already OK · ${st.missing} missing files`,
                    });
                    break;
                }
                if (st?.state === "error") {
                    toast.error(`Normalize failed: ${st.error || "unknown"}`);
                    break;
                }
            }
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Normalize failed"); }
        finally { setNormalizing(false); }
    };

    const doDriveSync = async () => {
        if (!canWrite) return;
        if (!window.confirm("Pull every image from the linked Google Drive folder into object storage and auto-link them to members?")) return;
        setDriveSyncing(true);
        try {
            await api.post(`/scoped/district-years/${yid}/photos/sync-drive`);
            toast.info("Drive sync started — this can take a couple of minutes for large folders.");
            const started = Date.now();
            while (Date.now() - started < 6 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 5000));
                const { data: st } = await api.get(`/scoped/district-years/${yid}/photos/sync-drive/status`);
                if (st?.state === "done") {
                    toast.success("Drive sync complete", {
                        description: `${st.downloaded} downloaded · ${st.skipped} already had · ${st.linked} linked · ${st.errors} errors`,
                    });
                    break;
                }
                if (st?.state === "error") {
                    toast.error(`Drive sync failed: ${st.error || "unknown"}`);
                    break;
                }
            }
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail) || "Drive sync failed"); }
        finally { setDriveSyncing(false); }
    };

    const doDirectoryPdf = async () => {
        // Iter 111 · Async background build with polling. The old sync
        // GET /directory.pdf tripped Cloudflare's ~60s edge timeout on
        // rosters with many photos; POST /start returns instantly with
        // a job id, we poll /status every 3s, then trigger the actual
        // download (and land a Resend email either way).
        setPdfLoading(true);
        try {
            const { data: start } = await api.post(`/scoped/district-years/${yid}/directory.pdf/start`);
            const jobId = start?.job_id;
            if (!jobId) { toast.error("PDF build failed to start"); return; }
            toast.info("Building directory PDF · we'll ping you when it's ready", { duration: 4000 });
            const started = Date.now();
            while (Date.now() - started < 15 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 3000));
                const { data: st } = await api.get(`/scoped/district-years/${yid}/directory.pdf/status/${jobId}`);
                if (st?.state === "ready") {
                    const dl = `${api.defaults.baseURL}/scoped/district-years/${yid}/directory.pdf/download/${jobId}`;
                    const a = document.createElement("a");
                    a.href = dl; a.download = st.filename || `directory-${yid}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    toast.success(`Directory PDF ready — ${st.member_count} member${st.member_count === 1 ? "" : "s"}`, {
                        description: st.filename,
                        action: { label: "Download again", onClick: () => window.open(dl, "_blank") },
                        duration: 15000,
                    });
                    return;
                }
                if (st?.state === "error" || st?.state === "expired") {
                    toast.error(`PDF failed: ${st.error || "unknown error"}`);
                    return;
                }
            }
            toast.warning("Still building after 15 min — check back shortly");
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Directory PDF failed");
        }
        finally { setPdfLoading(false); }
    };

    const doPushSheet = async () => {
        if (!canWrite) return;
        if (!window.confirm("Push every local member to the linked Google Sheet? Existing rows will be updated by phone, new rows will be appended.")) return;
        setPushing(true);
        try {
            const { data } = await api.post(`/scoped/district-years/${yid}/push-sheet`);
            toast.success("Push complete", {
                description: `${data.appended} added · ${data.updated} updated${data.failed ? ` · ${data.failed} failed` : ""}${data.skipped ? ` · ${data.skipped} skipped (missing phone)` : ""}`,
                duration: 6000,
            });
        } catch (e) {
            const msg = fmtErr(e?.response?.data?.detail) || "Push failed";
            const notConfigured = msg.toLowerCase().includes("no linked data source");
            toast.error(msg, notConfigured ? {
                description: "Open Settings and paste the Google Sheet URL for this year, then try again.",
                action: { label: "Open Settings", onClick: () => setTab("settings") },
                duration: 8000,
            } : undefined);
        }
        finally { setPushing(false); }
    };

    const doMigratePhotos = async () => {
        if (!window.confirm("Copy every local photo into durable object storage? Runs in the background — usually completes in under a minute.")) return;
        try {
            const { data } = await api.post(`/super/migrate-photos`);
            toast.success(data.started ? `Migration started (${data.total} files)` : "Migration already running", {
                description: "Poll /super/migrate-photos/status for progress",
                duration: 6000,
            });
            // Poll for completion in the background
            const poll = setInterval(async () => {
                try {
                    const { data: s } = await api.get(`/super/migrate-photos/status`);
                    if (s.status !== "running") {
                        clearInterval(poll);
                        toast.success("Photo migration complete", {
                            description: `${s.uploaded} uploaded${s.failed ? ` · ${s.failed} failed` : ""} of ${s.total}`,
                            duration: 8000,
                        });
                    }
                } catch { clearInterval(poll); }
            }, 4000);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Migration failed");
        }
    };

    const addCommittee = async () => {
        if (!newCommittee.trim()) return;
        try {
            await api.post(`/scoped/district-years/${yid}/committees`, { name: newCommittee.trim() });
            setNewCommittee("");
            toast.success("Committee added");
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const addSubCommittee = async (parent) => {
        const name = window.prompt(`Add sub-committee under "${parent.name}":`);
        if (!name || !name.trim()) return;
        try {
            await api.post(`/scoped/district-years/${yid}/committees`, {
                name: name.trim(), parent_id: parent.id,
            });
            toast.success("Sub-committee added");
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const renameCommittee = async (c) => {
        const newName = window.prompt("Rename committee:", c.name);
        if (!newName || newName === c.name) return;
        try {
            await api.patch(`/scoped/committees/${c.id}`, { name: newName, description: c.description || "" });
            toast.success("Renamed");
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const deleteCommittee = async (c) => {
        const affected = members.filter((m) => m.year_role.committee_assignments.some((a) => a.committee_id === c.id)).length;
        if (!window.confirm(`Delete "${c.name}"? ${affected} member${affected !== 1 ? "s are" : " is"} currently assigned.`)) return;
        try {
            await api.delete(`/scoped/committees/${c.id}`);
            toast.success("Committee deleted");
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    // iter 71 — inline sequence editor for committee export order.
    // Prompt-based rather than a full modal to keep the tree UI compact;
    // the value drives the `Generate Sheet` sort priority for each level.
    const updateCommitteeSeq = async (c) => {
        const raw = window.prompt(
            `Sequence number for "${c.name}" (lower = earlier in the exported sheet):`,
            String(c.display_order ?? ""),
        );
        if (raw === null) return;
        const next = parseInt(raw.trim(), 10);
        if (!Number.isFinite(next) || next < 0) { toast.error("Enter a positive whole number"); return; }
        if (next === (c.display_order || 0)) return;
        try {
            await api.patch(`/scoped/committees/${c.id}`, {
                name: c.name, description: c.description || "", display_order: next,
            });
            toast.success(`Sequence set to ${next}`);
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const removeMember = async (m) => {
        if (!window.confirm(`Remove ${m.name} from this year's roster and Google Sheet? Their profile is preserved for other years.`)) return;
        try {
            const { data } = await api.delete(`/scoped/year-roles/${m.year_role.id}`);
            toast.success("Removed", {
                description: data?.sheet_delete_scheduled ? "Sheet row deletion queued in background" : "Removed from year",
            });
            load();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const [sendingRid, setSendingRid] = useState(null);
    const [waMember, setWaMember] = useState(null);  // Member for the compose dialog

    // Iter 104 · Hybrid WhatsApp outreach — AiSensy first, wa.me fallback.
    // Both the row-level "Remind" nudge and the WhatsApp icon call the
    // backend `whatsapp-quick-send` endpoint which now tries an AiSensy
    // campaign send; on failure (missing key, provider rejection, network
    // error) the frontend auto-opens the wa.me link the backend hands
    // back so the admin's workflow is never stranded. Gupshup + Twilio
    // remain in the codebase behind their `is_enabled` guards and are
    // bypassed here per Iter 102 (manual-only until re-enabled).
    //
    // Iter 109 · Sync-popup trick — see MemberProfileDialog for details.
    // Modern browsers block window.open() fired after an awaited fetch;
    // we reserve the popup inside the click gesture and either close it
    // on success or navigate it to wa.me on failure.
    const openManualWhatsApp = async (m) => {
        const yrid = m?.year_role?.id;
        const token = m?.year_role?.form_token;
        if (!yrid || !token) {
            toast.error("No form link yet — generate one first");
            return;
        }
        const phone = String(m?.phone || "").replace(/\D+/g, "");
        if (!phone) {
            toast.error("Member phone missing — can't send WhatsApp");
            return;
        }
        const localFallbackUrl = buildDirectoryReminderUrl({
            governorName: context?.governor_name || "",
            districtNumber: context?.district_number || "",
            memberName: m?.name,
            phone,
            token,
        });
        const popup = window.open("", "_blank");
        try {
            const { data } = await api.post(`/scoped/year-roles/${yrid}/whatsapp-quick-send`);
            if (data?.ok && data?.channel === "aisensy") {
                if (popup && !popup.closed) popup.close();
                toast.success(`WhatsApp delivered to ${m?.name || "member"}`, {
                    description: `Sent via AiSensy to +91 ${phone.slice(-10)} · they'll get it within a few seconds.`,
                    duration: 6000,
                    className: "border-2 border-emerald-400 bg-emerald-50 text-emerald-900 shadow-lg",
                    icon: "✅",
                });
                return;
            }
            const url = data?.wa_url || localFallbackUrl;
            if (popup && !popup.closed) popup.location = url;
            else window.open(url, "_blank", "noopener,noreferrer");
            toast("AiSensy skipped — opened manual WhatsApp", {
                description: String(data?.detail || "backend fell back to manual").slice(0, 200),
                duration: 7000,
                className: "border-2 border-amber-400 bg-amber-50 text-amber-900 shadow-lg",
                icon: "⚠️",
                action: { label: "Open again", onClick: () => window.open(url, "_blank", "noopener,noreferrer") },
            });
        } catch (e) {
            if (popup && !popup.closed) popup.location = localFallbackUrl;
            else window.open(localFallbackUrl, "_blank", "noopener,noreferrer");
            const detail = e?.response?.data?.detail || e?.message || "server error";
            toast("AiSensy request failed — opened manual WhatsApp", {
                description: String(detail).slice(0, 200),
                duration: 7000,
                className: "border-2 border-amber-400 bg-amber-50 text-amber-900 shadow-lg",
                icon: "⚠️",
                action: { label: "Open again", onClick: () => window.open(localFallbackUrl, "_blank", "noopener,noreferrer") },
            });
        }
    };
    // Kept for legacy props; both list-level buttons now share the
    // hybrid handler so behaviour stays consistent.
    const remind = openManualWhatsApp;
    const openWhatsApp = openManualWhatsApp;

    const stats = useMemo(() => {
        const filled = members.filter((m) => (m.year_role.progress_pct || 0) >= 100).length;
        const verified = members.filter((m) => !!m.year_role.submitted_at).length;
        const avgProg = members.length ? Math.round(members.reduce((s, m) => s + (m.year_role.progress_pct || 0), 0) / members.length) : 0;
        return { total: members.length, filled, verified, avgProg };
    }, [members]);

    const commMap2 = useMemo(() => Object.fromEntries(committees.map((c) => [c.id, c])), [committees]);

    // Primary committee/position label for display in cards.
    // Prefer the server-composed `role_display` (which walks the committee
    // tree, strips standalone offices like "President", and falls back to
    // legacy `person.position` when no assignments exist). Fall back to the
    // old first-committee lookup only if the API is on an older version.
    const primaryLabelFor = (m) => {
        if (m.role_display) return m.role_display;
        const a = m.year_role.committee_assignments?.[0];
        if (!a) return m.position || "";
        const cname = commMap2[a.committee_id]?.name || "";
        const pos = (a.positions || [])[0] || "";
        return pos && cname ? `${cname}: ${pos}` : (pos || cname || "");
    };

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const searched = !s ? members : members.filter((m) => {
            if (m.name.toLowerCase().includes(s)) return true;
            if (String(m.phone || "").toLowerCase().includes(s)) return true;
            if (String(m.email || "").toLowerCase().includes(s)) return true;
            // Search inside committee names / positions
            return (m.year_role.committee_assignments || []).some((a) => {
                const cn = (commMap2[a.committee_id]?.name || "").toLowerCase();
                if (cn.includes(s)) return true;
                return (a.positions || []).some((p) => (p || "").toLowerCase().includes(s));
            });
        });
        const sorted = [...searched];
        sorted.sort((a, b) => {
            switch (sortBy) {
                case "progress_desc":
                    return (b.year_role.progress_pct || 0) - (a.year_role.progress_pct || 0)
                        || a.name.localeCompare(b.name);
                case "progress_asc":
                    return (a.year_role.progress_pct || 0) - (b.year_role.progress_pct || 0)
                        || a.name.localeCompare(b.name);
                case "committee": {
                    const la = primaryLabelFor(a).toLowerCase();
                    const lb = primaryLabelFor(b).toLowerCase();
                    return la.localeCompare(lb) || a.name.localeCompare(b.name);
                }
                case "name":
                default:
                    return a.name.localeCompare(b.name);
            }
        });
        return sorted;
    }, [q, sortBy, members, commMap2]);

    // Tab semantics (per user):
    //   Filled   = data is complete (progress_pct >= 100) — irrespective of source (sheet sync OR admin edit OR self-submit)
    //   Unfilled = data still has gaps (progress_pct < 100)
    //   Verified = member self-submitted through their mobile form (submitted_at is set)
    const filledMembers = filtered.filter((m) => (m.year_role.progress_pct || 0) >= 100);
    const unfilledMembers = filtered.filter((m) => (m.year_role.progress_pct || 0) < 100);
    const verifiedMembers = filtered.filter((m) => !!m.year_role.submitted_at);
    const visible = subTab === "filled" ? filledMembers
        : subTab === "verified" ? verifiedMembers
        : subTab === "all" ? filtered
        : unfilledMembers;

    const openProfile = (m, mode = "view") => {
        setProfileMode(mode);
        setProfileMember(m);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="year-view-page">
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
                    <div className="flex min-w-0 items-center gap-3">
                        {user?.role === "super_admin" && (
                            <Link to="/admin/platform" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-rotary-blue/20 text-rotary-blue hover:bg-rotary-blue hover:text-white" data-testid="year-back-btn">
                                <ChevronLeft className="h-4 w-4" />
                            </Link>
                        )}
                        <div className="min-w-0">
                            <p className="label-eyebrow leading-none">{user?.role === "super_admin" ? "Super Admin" : "District Admin"}</p>
                            <p className="rotary-heading truncate text-base leading-tight text-rotary-ink sm:text-lg" data-testid="year-header-context">
                                {context?.district_name ? `${context.district_name}` : "Year Workspace"}
                                {context?.year ? ` · Year ${context.year}` : ""}
                            </p>
                            {context?.district_number && (
                                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="year-header-rid">
                                    RI District {context.district_number}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={doLogout} className="shrink-0 rounded-full" data-testid="year-logout-btn">
                        <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
                    </Button>
                </div>
                {(user?.scopes || []).length > 1 && (
                    <div className="mx-auto max-w-7xl px-4 pb-3 -mt-1 sm:px-6">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Your assigned years:</span>
                            {user.scopes.map((s) => {
                                const active = String(s.year_id) === String(yid);
                                return (
                                    <button
                                        key={`${s.district_id}-${s.year}`}
                                        onClick={() => { if (!active && s.year_id) navigate(`/admin/year/${s.year_id}`); }}
                                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs transition ${
                                            active
                                                ? "border-rotary-blue bg-rotary-blue text-white"
                                                : "border-slate-200 bg-white text-slate-700 hover:border-rotary-blue hover:text-rotary-blue"
                                        }`}
                                        data-testid={`year-switcher-${s.district_number}-${s.year}`}
                                        disabled={!s.year_id}
                                    >
                                        RID {s.district_number} · {s.year}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </header>

            <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
                {context && lifecycle !== "active" && (
                    <LifecycleBanner
                        state={lifecycle}
                        year={context.year}
                        yearEnd={context.year_end}
                        readonlyUntil={context.readonly_until}
                        isSuper={isSuper}
                    />
                )}
                <div className="grid gap-4 grid-cols-2 sm:grid-cols-4 mb-6">
                    <StatBox label="Total members" value={stats.total} icon={<Users className="h-5 w-5" />} />
                    <StatBox label="Filled" value={stats.filled} icon={<CheckCircle2 className="h-5 w-5" />} accent />
                    <StatBox label="Verified by member" value={stats.verified} icon={<CheckCircle2 className="h-5 w-5" />} />
                    <StatBox label="Average profile complete" value={`${stats.avgProg}%`} icon={<Clock className="h-5 w-5" />} />
                </div>
                {/* Iter 100k · Consent audit widget. Click to see the
                    list of members who still owe a consent tick. */}
                {consentAudit && (
                    <button
                        type="button"
                        onClick={() => setConsentDialogOpen(true)}
                        className="mb-6 w-full text-left rounded-xl border bg-white p-4 shadow-sm transition hover:border-rotary-blue hover:shadow-md focus:outline-none focus:ring-2 focus:ring-rotary-blue"
                        data-testid="consent-audit-card"
                    >
                        <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                                <p className="label-eyebrow text-[10px]">Profile Consent Status</p>
                                <p className="rotary-heading text-sm mt-1">
                                    <span className="text-emerald-600 tabular-nums" data-testid="consent-audit-accepted">
                                        {consentAudit.accepted_count}
                                    </span>{" "}
                                    Accepted{" · "}
                                    <span className="text-rotary-cranberry tabular-nums" data-testid="consent-audit-pending">
                                        {consentAudit.pending_count}
                                    </span>{" "}
                                    Pending
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
                                <ShieldCheck className="h-4 w-4 text-rotary-blue" />
                                <span className="hidden sm:inline">Tap to see pending list</span>
                            </div>
                        </div>
                    </button>
                )}

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="mb-4">
                        <TabsTrigger value="members" data-testid="tab-year-members"><Users className="mr-1.5 h-4 w-4" /> Members</TabsTrigger>
                        <TabsTrigger value="committees" data-testid="tab-year-committees"><Layers className="mr-1.5 h-4 w-4" /> Committees</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-year-settings"><Settings className="mr-1.5 h-4 w-4" /> Settings</TabsTrigger>
                    </TabsList>

                    <TabsContent value="members">
                        {/* Admin tools row */}
                        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border bg-muted/20 p-2" data-testid="year-tools-row">
                            <span className="label-eyebrow pl-2 pr-1 hidden sm:inline">Tools</span>
                            {canWrite && (
                                <Button
                                    variant="outline" size="sm" className="rounded-full"
                                    onClick={doSyncSheet}
                                    disabled={syncing}
                                    data-testid="tool-sync-sheet-btn"
                                    title="Pull the latest rows from this year's linked data source into the app"
                                >
                                    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
                                    {syncing ? "Pulling…" : "Pull from sheet"}
                                </Button>
                            )}
                            {canWrite && (
                                <Button
                                    variant="outline" size="sm" className="rounded-full"
                                    onClick={doPushSheet}
                                    disabled={pushing}
                                    data-testid="tool-push-sheet-btn"
                                    title="Generate a fresh ordered export from Mongo to the year's tab — sorted by committee & position sequence"
                                >
                                    <Upload className={`mr-1.5 h-3.5 w-3.5 ${pushing ? "animate-pulse" : ""}`} />
                                    {pushing ? "Generating…" : "Generate sheet"}
                                </Button>
                            )}
                            {canWrite && (
                                <Button
                                    variant="outline" size="sm" className="rounded-full"
                                    onClick={doFixSheetHeaders}
                                    disabled={fixingHeaders}
                                    data-testid="tool-fix-headers-btn"
                                    title="Widen the year tab + Member Registry headers so the newest columns (e.g. Spouse Phone / Spouse Email) exist on this district's sheet"
                                >
                                    <Wand2 className={`mr-1.5 h-3.5 w-3.5 ${fixingHeaders ? "animate-pulse" : ""}`} />
                                    {fixingHeaders ? "Fixing…" : "Fix Sheet Headers"}
                                </Button>
                            )}
                            {canWrite && (
                                <Button
                                    variant="outline" size="sm" className="rounded-full"
                                    onClick={doImportRegistry}
                                    disabled={importingRegistry}
                                    data-testid="tool-import-registry-btn"
                                    title="Bulk-import every member from the shared Google Sheet Member Registry into this district's database"
                                >
                                    <Cloud className={`mr-1.5 h-3.5 w-3.5 ${importingRegistry ? "animate-pulse" : ""}`} />
                                    {importingRegistry ? "Importing…" : "Bulk Import from Registry"}
                                </Button>
                            )}
                            {isSuper && (
                                <Button
                                    variant="outline" size="sm" className="rounded-full"
                                    onClick={doDedupeSheet}
                                    disabled={dedupingSheet}
                                    data-testid="tool-dedupe-sheet-btn"
                                    title="Deduplicate Registry & year tab — keep most complete rows · unprotect · unhide"
                                >
                                    <Sparkles className={`mr-1.5 h-3.5 w-3.5 ${dedupingSheet ? "animate-pulse" : ""}`} />
                                    {dedupingSheet ? "Cleaning…" : "Clean Sheet"}
                                </Button>
                            )}
                            {canWrite && (
                                <>
                                    <input
                                        ref={importFileRef}
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        className="hidden"
                                        data-testid="tool-import-file-input"
                                        onChange={(e) => doImport(e.target.files?.[0])}
                                    />
                                    <Button
                                        variant="outline" size="sm" className="rounded-full"
                                        onClick={() => importFileRef.current?.click()}
                                        disabled={importing}
                                        data-testid="tool-import-btn"
                                        title="Import a CSV or XLSX (Name + Phone required)"
                                    >
                                        <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
                                        {importing ? "Importing…" : "Import CSV/XLSX"}
                                    </Button>
                                </>
                            )}
                            <div className="inline-flex flex-wrap items-end gap-2 border rounded-2xl px-2.5 py-1.5 bg-white" data-testid="tool-export-panel">
                                <div className="flex flex-col">
                                    <label className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5" htmlFor="export-status">Status</label>
                                    <select
                                        id="export-status"
                                        value={exportCompletion}
                                        onChange={(e) => setExportCompletion(e.target.value)}
                                        className="text-xs bg-transparent outline-none border rounded-md px-1.5 py-0.5"
                                        title="Filter by profile completion (100% = Filled)"
                                        data-testid="tool-export-completion-filter"
                                    >
                                        <option value="">All</option>
                                        <option value="filled">Filled</option>
                                        <option value="unfilled">Unfilled</option>
                                        <option value="verified">Verified</option>
                                    </select>
                                </div>
                                <div className="flex flex-col">
                                    <label className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5" htmlFor="export-committee">Category / Committee</label>
                                    <select
                                        id="export-committee"
                                        value={exportCommittee}
                                        onChange={(e) => setExportCommittee(e.target.value)}
                                        className="text-xs bg-transparent outline-none border rounded-md px-1.5 py-0.5 max-w-[200px]"
                                        title="Filter by 1st Level Committee (Category)"
                                        data-testid="tool-export-committee-filter"
                                    >
                                        <option value="">All Committees</option>
                                        {Array.from(
                                            new Set(
                                                committees
                                                    .filter((c) => !c.parent_id)
                                                    .map((c) => c.name),
                                            ),
                                        )
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                    </select>
                                </div>
                                <Button
                                    variant="ghost" size="sm" className="rounded-full h-7 px-2 mb-0.5"
                                    onClick={doExport}
                                    data-testid="tool-export-btn"
                                    title="Download this year's roster as XLSX (mirrors the current tab + search box + these filters)"
                                >
                                    <Download className="mr-1 h-3.5 w-3.5" /> XLSX
                                </Button>
                            </div>
                            <Button
                                variant="outline" size="sm" className="rounded-full"
                                onClick={() => setPhotoBrowserOpen(true)}
                                data-testid="tool-photo-browser-btn"
                                title="Browse every photo in object storage · link bulk uploads to members"
                                style={{ display: isSuper ? undefined : "none" }}
                            >
                                <Download className="mr-1.5 h-3.5 w-3.5 rotate-180" /> Photo library
                            </Button>
                            {/* Iter 100r · restored Photos ZIP tool. Downloads
                                a byte-identical ZIP of every member and spouse
                                photo for the roster (respects both filters).
                                Super-admin only — the backend also enforces. */}
                            <Button
                                variant="outline" size="sm" className="rounded-full"
                                onClick={doPhotosZip}
                                data-testid="tool-photos-zip-btn"
                                title="Download a ZIP of all member and spouse photos for the currently-filtered roster"
                                style={{ display: isSuper ? undefined : "none" }}
                            >
                                <Download className="mr-1.5 h-3.5 w-3.5" /> Photos ZIP
                            </Button>
                            <Button
                                variant="outline" size="sm" className="rounded-full"
                                onClick={doDirectoryPdf}
                                disabled={pdfLoading}
                                data-testid="tool-directory-pdf-btn"
                                title="Generate a PDF directory for this year"
                                style={{ display: isSuper ? undefined : "none" }}
                            >
                                <FileText className={`mr-1.5 h-3.5 w-3.5 ${pdfLoading ? "animate-pulse" : ""}`} />
                                {pdfLoading ? "Building…" : "Directory PDF"}
                            </Button>
                        </div>


                        {/* Toolbar */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="relative w-full sm:max-w-sm">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Search by name, phone, email, committee…"
                                        className="h-11 pl-9"
                                        data-testid="year-search-input"
                                    />
                                </div>
                                <Select value={sortBy} onValueChange={setSortBy}>
                                    <SelectTrigger className="h-11 w-full sm:w-56" data-testid="year-sort-select">
                                        <SelectValue placeholder="Sort by…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="name">Sort · Name (A–Z)</SelectItem>
                                        <SelectItem value="committee">Sort · Committee / Position</SelectItem>
                                        <SelectItem value="progress_desc">Sort · Progress (high → low)</SelectItem>
                                        <SelectItem value="progress_asc">Sort · Progress (low → high)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {canWrite && (subTab === "unfilled" || subTab === "all") && (
                                    <Button
                                        onClick={remindAll}
                                        disabled={remindingAll || loading || members.every((m) => (m.year_role.progress_pct || 0) >= 100)}
                                        variant="outline"
                                        className="rounded-full border-rotary-gold text-rotary-ink hover:bg-rotary-gold hover:text-rotary-ink"
                                        data-testid="year-remind-all-btn"
                                        title="Send a reminder to every unfilled member"
                                    >
                                        <MessageSquare className="mr-1.5 h-4 w-4" />
                                        {remindingAll ? "Sending…" : "Remind all unfilled"}
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        onClick={askAllToVerify}
                                        disabled={remindingAll || loading || members.length === 0}
                                        variant="outline"
                                        className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                                        data-testid="year-ask-verify-all-btn"
                                        title="Ask every member to open the form and verify their entry (data may have been entered by admin or from a spreadsheet)"
                                    >
                                        <ShieldCheck className="mr-1.5 h-4 w-4" />
                                        Ask all to verify
                                    </Button>
                                )}
                                <Button
                                    onClick={() => setAssignRoleOpen(true)}
                                    className={`rounded-full ${canWrite ? "bg-rotary-blue text-white hover:bg-rotary-sky" : "bg-muted text-muted-foreground hover:bg-muted"}`}
                                    data-testid="year-assign-role-btn"
                                    disabled={!canWrite}
                                    title={canWrite ? "Add a member — creates a new profile inline if the phone isn't in the registry, then assigns a role" : "Year is read-only"}
                                >
                                    <Plus className="mr-1.5 h-4 w-4" /> Add member
                                </Button>
                            </div>
                        </div>

                        {/* Sub-tabs Unfilled/Filled/All + View toggle */}
                        <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
                            <div className="flex items-center justify-between border-b">
                                <TabsList className="justify-start rounded-none bg-transparent p-0 h-auto border-0">
                                    <SubTab value="unfilled" active={subTab === "unfilled"} count={unfilledMembers.length}>Unfilled</SubTab>
                                    <SubTab value="filled" active={subTab === "filled"} count={filledMembers.length}>Filled</SubTab>
                                    <SubTab value="verified" active={subTab === "verified"} count={verifiedMembers.length}>Verified</SubTab>
                                    <SubTab value="all" active={subTab === "all"} count={filtered.length}>All</SubTab>
                                </TabsList>
                                <div className="pb-1.5 pr-1 flex items-center gap-1 rounded-full border bg-muted/40 p-0.5" role="group" aria-label="View mode">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode("grid")}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${viewMode === "grid" ? "bg-rotary-blue text-white shadow-sm" : "text-muted-foreground hover:text-rotary-ink"}`}
                                        data-testid="view-mode-grid"
                                        aria-pressed={viewMode === "grid"}
                                        title="Card grid view"
                                    >
                                        <LayoutGrid className="h-3.5 w-3.5" /> Cards
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode("list")}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${viewMode === "list" ? "bg-rotary-blue text-white shadow-sm" : "text-muted-foreground hover:text-rotary-ink"}`}
                                        data-testid="view-mode-list"
                                        aria-pressed={viewMode === "list"}
                                        title="Compact list view"
                                    >
                                        <ListIcon className="h-3.5 w-3.5" /> List
                                    </button>
                                </div>
                            </div>

                            {["unfilled", "filled", "verified", "all"].map((v) => (
                                <TabsContent key={v} value={v} className="mt-6">
                                    {viewMode === "grid" ? (
                                        <MemberGrid
                                            members={visible}
                                            loading={loading}
                                            onOpen={(m) => openProfile(m, "view")}
                                            onEdit={(m) => openProfile(m, "edit")}
                                            onRemind={remind}
                                            onWhatsApp={openWhatsApp}
                                            sendingRid={sendingRid}
                                            primaryLabel={primaryLabelFor}
                                            showReminder={(v === "unfilled" || v === "all") && canWrite}
                                            committees={committees}
                                            canWrite={canWrite}
                                        />
                                    ) : (
                                        <MemberList
                                            members={visible}
                                            loading={loading}
                                            onOpen={(m) => openProfile(m, "view")}
                                            onEdit={(m) => openProfile(m, "edit")}
                                            onRemind={remind}
                                            onWhatsApp={openWhatsApp}
                                            onRemove={removeMember}
                                            sendingRid={sendingRid}
                                            commMap={commMap}
                                            canWrite={canWrite}
                                        />
                                    )}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </TabsContent>

                    <TabsContent value="committees">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                            <h2 className="rotary-heading text-xl">Committees</h2>
                            {canWrite && (
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        className="hidden"
                                        data-testid="committee-hierarchy-upload-input"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = "";
                                            if (!file) return;
                                            const confirmMsg = `Upload "${file.name}" as the committee hierarchy for this year?\n\n` +
                                                "This will ADD new committee paths from the file and REMOVE existing " +
                                                "committees that are missing from the file — except any that are still " +
                                                "referenced by member roles (those are kept).\n\nContinue?";
                                            if (!window.confirm(confirmMsg)) return;
                                            const fd = new FormData();
                                            fd.append("file", file);
                                            try {
                                                const { data } = await api.post(
                                                    `/scoped/district-years/${yid}/committees/upload`,
                                                    fd,
                                                    { headers: { "Content-Type": "multipart/form-data" } },
                                                );
                                                toast.success("Hierarchy synced", {
                                                    description: `Added ${data.added} · Removed ${data.removed} · Kept in use ${data.kept_in_use.length}` +
                                                        (data.errors?.length ? ` · ${data.errors.length} row error(s)` : ""),
                                                });
                                                load();
                                            } catch (err) {
                                                toast.error(err?.response?.data?.detail || "Upload failed");
                                            }
                                        }}
                                    />
                                    <span
                                        className="inline-flex items-center rounded-full border border-rotary-blue px-3 py-1.5 text-sm text-rotary-blue hover:bg-rotary-blue hover:text-white transition-colors"
                                        data-testid="committee-hierarchy-upload-btn"
                                    >
                                        <Upload className="mr-1.5 h-4 w-4" /> Import from Excel
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="rounded-xl border bg-white overflow-hidden">
                            {canWrite && (
                                <div className="border-b px-4 py-3 flex gap-2">
                                    <Input value={newCommittee} onChange={(e) => setNewCommittee(e.target.value)} placeholder="New top-level committee name" onKeyDown={(e) => e.key === "Enter" && addCommittee()} data-testid="committee-new-input" />
                                    <Button onClick={addCommittee} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="committee-add-btn">
                                        <Plus className="mr-1.5 h-4 w-4" /> Add
                                    </Button>
                                </div>
                            )}
                            <CommitteeTree
                                committees={committees}
                                members={members}
                                onAddChild={addSubCommittee}
                                onRename={renameCommittee}
                                onDelete={deleteCommittee}
                                onEditSeq={updateCommitteeSeq}
                                onOpenMember={(m) => openProfile(m, "view")}
                                canWrite={canWrite}
                            />
                        </div>
                        {/* iter 71 — Positions with sequence numbers. Feeds the
                            Generate Sheet sort priority alongside committees. */}
                        <PositionsPanel yid={yid} canWrite={canWrite} />
                    </TabsContent>
                    <TabsContent value="settings">
                        <SettingsSection yid={yid} apiBase={process.env.REACT_APP_BACKEND_URL} />
                    </TabsContent>
                </Tabs>
            </main>

            <AssignRoleDialog
                open={assignRoleOpen}
                onOpenChange={setAssignRoleOpen}
                yid={yid}
                onSaved={(opts) => {
                    // The dialog signals `keepOpen: true` after a new-member
                    // create flow so the admin can trigger the WhatsApp
                    // profile-completion message before closing.
                    if (!opts?.keepOpen) setAssignRoleOpen(false);
                    load();
                }}
            />
            <EditAssignmentsDialog member={editMember} onOpenChange={(o) => !o && setEditMember(null)} committees={committees} onSaved={() => { setEditMember(null); load(); }} />
            <PhotoDownloadDialog
                open={photoDialogOpen}
                onOpenChange={setPhotoDialogOpen}
                initialDistrictId={context?.district_id || ""}
                initialYear={context?.year || ""}
            />
            <PhotoBrowserDialog
                open={photoBrowserOpen}
                onOpenChange={setPhotoBrowserOpen}
            />
            {/* Iter 100k · Consent audit dialog. Lists members who
                haven't accepted the profile consent yet so admins
                can chase them from the widget. */}
            <Dialog open={consentDialogOpen} onOpenChange={setConsentDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="consent-audit-dialog">
                    <DialogHeader>
                        <DialogTitle className="rotary-heading text-xl flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-rotary-blue" /> Profile Consent — Pending Members
                        </DialogTitle>
                    </DialogHeader>
                    {consentAudit && (
                        <div className="space-y-3 pt-2">
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-lg border bg-emerald-50 p-3">
                                    <p className="text-2xl rotary-heading text-emerald-600 tabular-nums">{consentAudit.accepted_count}</p>
                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Accepted</p>
                                </div>
                                <div className="rounded-lg border bg-rose-50 p-3">
                                    <p className="text-2xl rotary-heading text-rotary-cranberry tabular-nums">{consentAudit.pending_count}</p>
                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Pending</p>
                                </div>
                                <div className="rounded-lg border bg-slate-50 p-3">
                                    <p className="text-2xl rotary-heading text-slate-700 tabular-nums">{consentAudit.total}</p>
                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total</p>
                                </div>
                            </div>
                            {consentAudit.pending?.length ? (
                                <>
                                    {canWrite && (
                                        <div className="flex justify-end">
                                            <Button
                                                onClick={doGrantConsentAll}
                                                disabled={grantingConsent}
                                                className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                                data-testid="consent-audit-grant-all-btn"
                                            >
                                                {grantingConsent ? "Granting…" : `Grant consent to all ${consentAudit.pending_count} pending`}
                                            </Button>
                                        </div>
                                    )}
                                    <ul className="divide-y rounded-lg border bg-white" data-testid="consent-audit-pending-list">
                                    {consentAudit.pending.map((p) => (
                                        <li key={p.year_role_id} className="flex items-center justify-between gap-3 p-3">
                                            <div className="min-w-0">
                                                <p className="rotary-heading text-sm truncate">{p.name || "—"}</p>
                                                <p className="text-xs text-muted-foreground truncate">{p.phone} · {p.club_name || "—"} · {p.progress_pct}% profile</p>
                                            </div>
                                            {p.form_token && (
                                                <a
                                                    href={`/form/${p.form_token}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="shrink-0 text-xs text-rotary-blue hover:underline"
                                                    data-testid={`consent-audit-open-form-${p.phone}`}
                                                >
                                                    Open form ↗
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                    </ul>
                                </>
                            ) : (
                                <p className="rounded-lg border bg-emerald-50 p-4 text-center text-sm text-emerald-700" data-testid="consent-audit-all-accepted">
                                    Every member on this year's roster has accepted the profile consent. 🎉
                                </p>
                            )}
                        </div>
                    )}
                    <DialogFooter className="pt-2">
                        <Button variant="outline" onClick={() => setConsentDialogOpen(false)} className="rounded-full">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <WhatsAppComposeDialog
                open={!!waMember}
                onOpenChange={(o) => !o && setWaMember(null)}
                member={waMember}
                style="progress"
            />
            <MemberProfileDialog
                open={!!profileMember}
                onOpenChange={(o) => !o && setProfileMember(null)}
                member={profileMember}
                committees={committees}
                initialMode={profileMode}
                canWrite={canWrite}
                governorName={context?.governor_name || ""}
                districtNumber={context?.district_number || ""}
                onToggleVerified={toggleVerified}
                onUpdated={(data) => {
                    // data is the merged {...person, year_role} — update local list and current dialog
                    setMembers((rows) => rows.map((r) => (r.year_role.id === data.year_role.id
                        ? { ...r, ...data }
                        : r)));
                    setProfileMember((prev) => (prev ? { ...prev, ...data } : prev));
                }}
                onEditCommittees={() => {
                    if (profileMember) {
                        setEditMember(profileMember);
                        setProfileMember(null);
                    }
                }}
                onRemove={async () => {
                    if (!profileMember) return;
                    const m = profileMember;
                    setProfileMember(null);
                    await removeMember(m);
                }}
            />
        </div>
    );
}

function StatBox({ label, value, icon, accent }) {
    return (
        <div className={`rounded-xl border p-4 sm:p-6 ${accent ? "bg-rotary-blue text-white border-transparent" : "bg-white"}`}>
            <div className="flex items-center justify-between gap-2">
                <span className={`label-eyebrow text-[10px] sm:text-xs ${accent ? "text-white/70" : ""}`}>{label}</span>
                {icon && (
                    <span className={`grid h-7 w-7 sm:h-8 sm:w-8 shrink-0 place-items-center rounded-full ${accent ? "bg-white/10 text-rotary-gold" : "bg-rotary-blue/10 text-rotary-blue"}`}>
                        {icon}
                    </span>
                )}
            </div>
            <p className={`mt-2 sm:mt-3 rotary-heading text-2xl sm:text-4xl ${accent ? "text-white" : "text-rotary-ink"}`}>{value}</p>
        </div>
    );
}

function LifecycleBanner({ state, year, yearEnd, readonlyUntil, isSuper }) {
    const bg = state === "disabled" ? "bg-rotary-cranberry/10 border-rotary-cranberry/40" : "bg-rotary-gold/10 border-rotary-gold/40";
    const iconWrap = state === "disabled" ? "bg-rotary-cranberry text-white" : "bg-rotary-gold text-rotary-ink";
    const title = state === "disabled"
        ? `Year ${year} is closed`
        : `Year ${year} ended — read-only mode`;
    const body = state === "disabled"
        ? `The 30-day grace period ended on ${readonlyUntil}. Contact your platform administrator to reopen this year.`
        : `Year ended on ${yearEnd}. You have read-only access until ${readonlyUntil}. Edits, reminders, and new additions are disabled${isSuper ? " for district admins" : ""}.`;
    return (
        <div className={`mb-6 rounded-xl border ${bg} p-4 flex items-start gap-3`} data-testid={`lifecycle-banner-${state}`}>
            <span className={`grid h-9 w-9 place-items-center rounded-full ${iconWrap} shrink-0`}>
                <Clock className="h-4 w-4" />
            </span>
            <div className="min-w-0">
                <p className="rotary-heading text-sm text-rotary-ink">{title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{body}</p>
                {isSuper && (
                    <p className="mt-1 text-[11px] text-rotary-blue font-medium">
                        You are viewing as Super Admin — writes remain enabled for you.
                    </p>
                )}
            </div>
        </div>
    );
}

function SubTab({ value, active, count, children }) {
    return (
        <TabsTrigger
            value={value}
            data-testid={`year-subtab-${value}`}
            className={`rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-semibold uppercase tracking-wider data-[state=active]:border-rotary-blue data-[state=active]:text-rotary-blue data-[state=active]:shadow-none ${
                active ? "" : "text-muted-foreground"
            }`}
        >
            {children}
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] ${active ? "bg-rotary-blue text-white" : "bg-muted text-muted-foreground"}`}>
                {count}
            </span>
        </TabsTrigger>
    );
}

function initialsOf(name) {
    return (name || "?")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function MemberGrid({ members, loading, onOpen, onEdit, onRemind, onWhatsApp, sendingRid, primaryLabel, showReminder, canWrite, committees = [] }) {
    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-8 text-sm text-muted-foreground" data-testid="member-grid-loading">
                Loading members…
            </div>
        );
    }
    if (!members.length) {
        return (
            <div className="rounded-xl border bg-white p-8 text-sm text-muted-foreground" data-testid="member-grid-empty">
                No members in this group.
            </div>
        );
    }
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="member-grid">
            {members.map((m) => (
                <MemberCard
                    key={m.year_role.id}
                    m={m}
                    onOpen={onOpen}
                    onEdit={onEdit}
                    onRemind={onRemind}
                    onWhatsApp={onWhatsApp}
                    sending={sendingRid === m.year_role.id}
                    primaryLabel={primaryLabel}
                    showReminder={showReminder}
                    committees={committees}
                    canWrite={canWrite}
                />
            ))}
        </div>
    );
}

function statusChip(submitted, verified, progress) {
    if (submitted) return { label: "Filled", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (verified || progress >= 100) return { label: "Ready", cls: "bg-sky-50 text-sky-700 border-sky-200" };
    return { label: "Pending", cls: "bg-rotary-gold/10 text-rotary-ink border-rotary-gold/40" };
}

function MemberCard({ m, onOpen, onEdit, onRemind, onWhatsApp, sending, primaryLabel, showReminder, canWrite, committees = [] }) {
    const submitted = !!m.year_role.submitted_at;
    const verified = !!m.year_role.verified_at;
    const progress = m.year_role.progress_pct || 0;
    const photo = m.photo_filename ? uploadUrl(m.photo_filename, m.updated_at) : "";
    const formToken = m.year_role.form_token || "";
    const label = primaryLabel ? primaryLabel(m) : "";
    // Full committee-path role chips, deduplicated — visual only.
    const roleChips = buildRolePathChips(m, committees);
    const isLeadership = hasClubLeadership(m.year_role.committee_assignments);
    const leadershipPos = firstLeadershipPosition(m.year_role.committee_assignments);
    // Suppress the gold leadership pill when a role chip already carries the
    // same position — the user's "no duplicate / no overlap" rule.
    const leadershipCovered = !!leadershipPos && roleChips.some((chip) => chip.toLowerCase().startsWith(leadershipPos.toLowerCase()));
    // Card background telegraphs profile status — no text chips needed.
    // Completed = soft emerald · Incomplete = soft amber · default white.
    const isComplete = submitted || verified || progress >= 100;
    const cardBg = isComplete
        ? "bg-emerald-50 border-emerald-200"
        : "bg-amber-50/50 border-amber-200/60";

    return (
        <div
            className={`group rounded-xl border p-5 hover:-translate-y-1 hover:shadow-md ${cardBg}`}
            style={{ transition: "transform 220ms ease, box-shadow 220ms ease" }}
            data-testid={`member-card-${m.phone}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    {photo ? (
                        <img
                            src={photo}
                            alt={m.name}
                            className="h-12 w-12 rounded-full object-cover border shrink-0"
                            onError={(e) => {
                                // Iter 58: if the stored filename points at a
                                // file that isn't in Object Storage (legacy
                                // `image1_XXX.jpg` names lost during a past
                                // migration), fall back to the initials tile
                                // rather than showing a broken-image icon.
                                e.currentTarget.style.display = "none";
                                const sib = e.currentTarget.nextElementSibling;
                                if (sib) sib.style.display = "grid";
                            }}
                        />
                    ) : null}
                    <div
                        className="h-12 w-12 rounded-full bg-rotary-blue text-white grid place-items-center rotary-heading text-sm shrink-0"
                        style={{ display: photo ? "none" : "grid" }}
                    >
                        {initialsOf(m.name)}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => onOpen(m)}
                                className="rotary-heading text-lg text-rotary-ink hover:text-rotary-blue block truncate max-w-[220px] text-left"
                                data-testid={`member-name-link-${m.phone}`}
                            >
                                {m.name}
                            </button>
                            {verified && (
                                <BadgeCheck
                                    className="h-4 w-4 text-emerald-600 shrink-0"
                                    aria-label="Verified"
                                    data-testid={`member-verified-${m.phone}`}
                                >
                                    <title>Data verified</title>
                                </BadgeCheck>
                            )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground truncate">{m.phone}</p>
                        {m.club_name && (
                            <p className="mt-1 text-xs truncate" data-testid={`member-home-club-${m.phone}`}>
                                <span className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Home Club:</span>{" "}
                                <span className="text-rotary-ink">{m.club_name}</span>
                            </p>
                        )}
                        {roleChips.length > 0 ? (
                            <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`member-role-chips-${m.phone}`}>
                                {roleChips.map((chip, i) => (
                                    <span
                                        key={i}
                                        className="rotary-chip text-[10px] bg-rotary-blue/10 border-rotary-blue/20 text-rotary-blue"
                                        title={chip}
                                        data-testid={`member-role-chip-${m.phone}-${i}`}
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        ) : label && (
                            <p className="mt-1 text-xs text-muted-foreground truncate">
                                <span className="text-rotary-blue">{label}</span>
                            </p>
                        )}
                        {isLeadership && !leadershipCovered && (
                            <span
                                className="mt-1 inline-flex items-center gap-1 rounded-full bg-rotary-gold/15 border border-rotary-gold/40 text-rotary-ink px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider max-w-full"
                                data-testid={`member-club-leadership-${m.phone}`}
                                title={m.club_name ? `${leadershipPos || "Club leader"} · ${m.club_name}` : (leadershipPos || "Club leader")}
                            >
                                <span className="whitespace-nowrap">{leadershipPos || "Club leadership"}</span>
                                {m.club_name && (
                                    <>
                                        <span aria-hidden className="opacity-40">·</span>
                                        <span className="truncate normal-case tracking-normal font-medium max-w-[140px]">{m.club_name}</span>
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    {/* Status is telegraphed by card background color (green =
                        completed, amber = pending). Only surface the verified
                        badge, which is a positive signal worth showing. */}
                    {verified && (
                        <span
                            className="rotary-chip whitespace-nowrap bg-emerald-500 text-white border-transparent inline-flex items-center gap-1"
                            data-testid={`member-verified-chip-${m.phone}`}
                            title="Entry data has been verified"
                        >
                            <BadgeCheck className="h-3 w-3" /> Verified
                        </span>
                    )}
                </div>
            </div>

            <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                    <span className="label-eyebrow">Profile</span>
                    <span className="font-semibold text-rotary-ink">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2 bg-muted [&>div]:rotary-progress-fill" data-testid={`member-progress-${m.phone}`} />
            </div>

            <div className="mt-5 flex items-center gap-2">
                <Button
                    variant="outline"
                    onClick={() => onOpen(m)}
                    className="flex-1 rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                    data-testid={`view-member-btn-${m.phone}`}
                >
                    View <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
                {canWrite && (
                    <Button
                        variant="outline"
                        onClick={() => onEdit(m)}
                        className="rounded-full h-9 w-9 p-0 border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                        data-testid={`edit-member-btn-${m.phone}`}
                        title="Quick edit"
                        aria-label={`Edit ${m.name}`}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                )}
                {showReminder && (
                    <>
                        <Button
                            onClick={() => onRemind(m)}
                            disabled={!formToken}
                            className={`rounded-full ${formToken ? "bg-rotary-gold text-rotary-ink hover:bg-rotary-gold/85" : "bg-muted text-muted-foreground pointer-events-none"}`}
                            data-testid={`send-reminder-btn-${m.phone}`}
                            title={formToken ? "Open WhatsApp Web with a governor-signed reminder pre-composed" : "No form link generated"}
                        >
                            <MessageSquare className="mr-1.5 h-4 w-4" />
                            Remind
                        </Button>
                        <Button
                            onClick={() => onWhatsApp?.(m)}
                            disabled={!formToken}
                            className={`inline-flex items-center justify-center rounded-full px-3.5 h-9 text-sm font-medium ${formToken ? "bg-[#25D366] text-white hover:bg-[#1ebe57]" : "bg-muted text-muted-foreground pointer-events-none"}`}
                            data-testid={`send-whatsapp-btn-${m.phone}`}
                            title={formToken ? "Open WhatsApp Web with a governor-signed reminder pre-composed" : "No form link generated"}
                            aria-label={`Open WhatsApp for ${m.name}`}
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function MemberList({ members, loading, onOpen, onEdit, onRemind, onRemove, sendingRid, commMap, canWrite }) {
    if (loading) {
        return <p className="text-sm text-muted-foreground p-4">Loading…</p>;
    }
    if (!members.length) {
        return (
            <div className="rounded-xl border bg-white p-8 text-sm text-muted-foreground" data-testid="member-list-empty">
                No members in this group.
            </div>
        );
    }

    const ROW_HEIGHT = 68;
    // Cap the visible viewport so short lists collapse naturally; long lists get scroll+virtualization.
    const viewportHeight = Math.min(members.length * ROW_HEIGHT, 640);

    const rowProps = { members, onOpen, onEdit, onRemind, onRemove, sendingRid, commMap, canWrite };

    return (
        <div className="overflow-hidden rounded-xl border bg-white" data-testid="member-list">
            {/* Header (sticky look) */}
            <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,4fr)_minmax(0,3fr)_minmax(0,2fr)] items-center gap-4 border-b bg-muted/30 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                <div>Name · Phone</div>
                <div>Committees · Positions</div>
                <div>Progress · Status</div>
                <div className="text-right">Actions</div>
            </div>

            <VList
                rowComponent={VirtualMemberRow}
                rowCount={members.length}
                rowHeight={ROW_HEIGHT}
                rowProps={rowProps}
                style={{ height: viewportHeight, width: "100%" }}
            />

            {members.length > 12 && (
                <div className="border-t bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
                    Showing {members.length} members · scroll within the list
                </div>
            )}
        </div>
    );
}

function VirtualMemberRow({ index, style, members, onOpen, onEdit, onRemind, onRemove, sendingRid, commMap, canWrite }) {
    const m = members[index];
    if (!m) return null;
    const submitted = !!m.year_role.submitted_at;
    const verified = !!m.year_role.verified_at;
    const progress = m.year_role.progress_pct || 0;
    const cm = commMap || {};
    const assignments = m.year_role.committee_assignments || [];
    // Prefer server-composed role_display (walks committee tree, strips
    // standalone offices, falls back to legacy person.position). Only compute
    // a client-side chip when the server didn't provide role_display.
    let primaryChip = m.role_display || "";
    if (!primaryChip && assignments.length) {
        const firstAssign = assignments[0];
        primaryChip = `${cm[firstAssign.committee_id]?.name || "?"}${(firstAssign.positions || []).length ? `: ${(firstAssign.positions || []).join(", ")}` : ""}`;
    }
    const extra = Math.max(0, assignments.length - 1);
    const isLeadership = hasClubLeadership(assignments);
    const leadershipPos = firstLeadershipPosition(assignments);

    const isComplete = submitted || verified || progress >= 100;
    const rowBg = isComplete
        ? "bg-emerald-50/60 hover:bg-emerald-50"
        : "bg-amber-50/30 hover:bg-amber-50/60";

    return (
        <div
            style={style}
            className={`grid grid-cols-[minmax(0,3fr)_minmax(0,4fr)_minmax(0,3fr)_minmax(0,2fr)] items-center gap-4 border-b px-4 ${rowBg}`}
            data-testid={`year-member-row-${m.phone}`}
        >
            <button
                type="button"
                onClick={() => onOpen(m)}
                className="text-left group min-w-0"
                data-testid={`year-member-open-${m.phone}`}
            >
                <p className="font-medium text-rotary-blue group-hover:underline truncate flex items-center gap-1">
                    <span className="truncate">{m.name}</span>
                    {verified && (
                        <BadgeCheck
                            className="h-3.5 w-3.5 text-emerald-600 shrink-0"
                            aria-label="Verified"
                            data-testid={`year-member-verified-${m.phone}`}
                        />
                    )}
                </p>
                <p className="text-xs text-muted-foreground truncate">{m.phone}</p>
            </button>

            <div className="min-w-0">
                {primaryChip && (
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                        <span
                            className="rotary-chip text-[11px] bg-rotary-blue/10 border-rotary-blue/20 text-rotary-blue truncate max-w-full"
                            title={primaryChip}
                        >
                            {primaryChip}
                        </span>
                        {isLeadership && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-rotary-gold/15 border border-rotary-gold/40 text-rotary-ink px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider max-w-full"
                                data-testid={`year-member-club-leadership-${m.phone}`}
                                title={m.club_name ? `${leadershipPos || "Club leader"} · ${m.club_name}` : (leadershipPos || "Club leader")}
                            >
                                <span className="whitespace-nowrap">{leadershipPos || "Club leadership"}</span>
                                {m.club_name && (
                                    <>
                                        <span aria-hidden className="opacity-40">·</span>
                                        <span className="truncate normal-case tracking-normal font-medium max-w-[120px]">{m.club_name}</span>
                                    </>
                                )}
                            </span>
                        )}
                        {extra > 0 && (
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                +{extra} more
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <Progress value={progress} className="h-1.5 flex-1 bg-muted [&>div]:rotary-progress-fill" />
                    <span className={`text-xs font-medium tabular-nums ${progress >= 80 ? "text-emerald-700" : progress >= 50 ? "text-amber-700" : "text-rotary-cranberry"}`}>
                        {progress}%
                    </span>
                </div>
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                    {(() => {
                        const chip = statusChip(submitted, verified, progress);
                        // Card row bg color already indicates completion state,
                        // so we skip both Pending and Ready chips. Only surface
                        // "Filled" as a positive confirmation.
                        if (chip.label !== "Filled") return null;
                        return (
                            <span className={`inline-flex rotary-chip text-[10px] ${chip.cls}`}>
                                {chip.label}
                            </span>
                        );
                    })()}
                    {verified && (
                        <span
                            className="inline-flex items-center gap-0.5 rotary-chip text-[10px] bg-emerald-500 text-white border-transparent"
                            data-testid={`year-member-verified-chip-${m.phone}`}
                            title="Data verified"
                        >
                            <BadgeCheck className="h-2.5 w-2.5" /> Verified
                        </span>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => onRemind(m)} disabled={!canWrite || !m.year_role.form_token} title={!canWrite ? "Year is read-only" : (m.year_role.form_token ? "Open WhatsApp Web with a governor-signed reminder pre-composed" : "No form link generated")} data-testid={`year-member-remind-${m.phone}`}>
                    <Send className="h-4 w-4 text-rotary-blue" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onEdit(m)} disabled={!canWrite} title={canWrite ? "Quick edit" : "Year is read-only"} data-testid={`year-member-edit-${m.phone}`}>
                    <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onRemove(m)} disabled={!canWrite} title={canWrite ? "Remove from year" : "Year is read-only"} data-testid={`year-member-delete-${m.phone}`}>
                    <Trash2 className="h-4 w-4 text-rotary-cranberry" />
                </Button>
            </div>
        </div>
    );
}

function SettingsSection({ yid, apiBase }) {
    const [data, setData] = useState(null);
    const [savingD, setSavingD] = useState(false);
    const [savingY, setSavingY] = useState(false);

    const load = async () => {
        try {
            const { data: d } = await api.get(`/scoped/district-years/${yid}/settings`);
            setData(d);
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, [yid]);

    if (!data) return <p className="text-sm text-muted-foreground">Loading settings…</p>;

    const saveDistrict = async (e) => {
        e.preventDefault();
        setSavingD(true);
        try {
            await api.patch(`/scoped/districts/${data.district.id}/settings`, {
                display_name: data.district.display_name,
                tagline: data.district.tagline,
                contact_email: data.district.contact_email,
                contact_phone: data.district.contact_phone,
                whatsapp_reminder_template: data.district.whatsapp_reminder_template || "",
                whatsapp_verify_template: data.district.whatsapp_verify_template || "",
                whatsapp_thankyou_template: data.district.whatsapp_thankyou_template || "",
            });
            toast.success("District settings saved");
        } catch (e2) { toast.error(fmtErr(e2?.response?.data?.detail)); }
        finally { setSavingD(false); }
    };

    const saveYear = async (e) => {
        e.preventDefault();
        setSavingY(true);
        try {
            await api.patch(`/scoped/district-years/${yid}/settings`, {
                governor_name: data.year.governor_name,
                governor_message: data.year.governor_message,
                theme_line: data.year.theme_line,
                sheet_url: (data.year.sheet_url || "").trim(),
            });
            toast.success("Year settings saved");
        } catch (e2) { toast.error(fmtErr(e2?.response?.data?.detail)); }
        finally { setSavingY(false); }
    };

    const uploadCrest = async (file) => {
        const fd = new FormData();
        fd.append("photo", file);
        try {
            const { data: r } = await api.post(`/scoped/districts/${data.district.id}/crest`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setData({ ...data, district: { ...data.district, crest_url: r.crest_url } });
            toast.success("Crest uploaded");
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const uploadGov = async (file) => {
        const fd = new FormData();
        fd.append("photo", file);
        try {
            const { data: r } = await api.post(`/scoped/district-years/${yid}/governor-photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setData({ ...data, year: { ...data.year, governor_photo_url: r.governor_photo_url } });
            toast.success("Governor photo uploaded");
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    };

    const imgUrl = (p) => p ? (p.startsWith("http") ? p : `${apiBase}${p}`) : "";

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={saveDistrict} className="rounded-xl border bg-white p-6 space-y-4">
                <h3 className="rotary-heading text-lg text-rotary-ink">District identity</h3>
                <div className="flex items-center gap-4">
                    <div className="grid h-20 w-20 place-items-center rounded-xl bg-muted overflow-hidden border">
                        {data.district.crest_url ? (
                            <img src={imgUrl(data.district.crest_url)} alt="Crest" className="h-full w-full object-contain" />
                        ) : (
                            <span className="text-xs text-muted-foreground">No crest</span>
                        )}
                    </div>
                    <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCrest(e.target.files[0])} data-testid="crest-upload-input" />
                        <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-muted"><Upload className="h-3.5 w-3.5" /> Upload crest</span>
                    </label>
                </div>
                <div><Label className="label-eyebrow">Display name</Label>
                    <Input value={data.district.display_name} onChange={(e) => setData({ ...data, district: { ...data.district, display_name: e.target.value } })} data-testid="settings-district-name" /></div>
                <div><Label className="label-eyebrow">Tagline</Label>
                    <Input value={data.district.tagline} onChange={(e) => setData({ ...data, district: { ...data.district, tagline: e.target.value } })} /></div>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label className="label-eyebrow">Contact email</Label>
                        <Input type="email" value={data.district.contact_email} onChange={(e) => setData({ ...data, district: { ...data.district, contact_email: e.target.value } })} /></div>
                    <div><Label className="label-eyebrow">Contact phone</Label>
                        <Input value={data.district.contact_phone} onChange={(e) => setData({ ...data, district: { ...data.district, contact_phone: e.target.value } })} /></div>
                </div>
                <div className="rounded-lg border bg-slate-50 p-3 space-y-3">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-rotary-blue" />
                        <h4 className="text-sm font-semibold text-rotary-ink">WhatsApp reminder templates</h4>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                        Placeholders you can use: <code className="rounded bg-white px-1 py-0.5 text-[10px]">{"{name}"}</code>{" "}
                        <code className="rounded bg-white px-1 py-0.5 text-[10px]">{"{year}"}</code>{" "}
                        <code className="rounded bg-white px-1 py-0.5 text-[10px]">{"{progress}"}</code>{" "}
                        <code className="rounded bg-white px-1 py-0.5 text-[10px]">{"{link}"}</code>. Leave blank to use the default wording.
                    </p>
                    <div>
                        <Label className="label-eyebrow">Reminder message (&ldquo;Update your profile&rdquo;)</Label>
                        <textarea
                            value={data.district.whatsapp_reminder_template || ""}
                            onChange={(e) => setData({ ...data, district: { ...data.district, whatsapp_reminder_template: e.target.value } })}
                            rows={3}
                            className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                            placeholder="Hi {name}, your directory profile for {year} is {progress}% complete. Update it here: {link}"
                            data-testid="settings-whatsapp-reminder"
                        />
                    </div>
                    <div>
                        <Label className="label-eyebrow">Verify message (&ldquo;Please confirm your entry&rdquo;)</Label>
                        <textarea
                            value={data.district.whatsapp_verify_template || ""}
                            onChange={(e) => setData({ ...data, district: { ...data.district, whatsapp_verify_template: e.target.value } })}
                            rows={3}
                            className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                            placeholder="Hi {name}, please review and verify your directory entry for {year}. Tap Submit even if the details look correct: {link}"
                            data-testid="settings-whatsapp-verify"
                        />
                    </div>
                    <div>
                        <Label className="label-eyebrow">Thank-you message (fires when a member submits)</Label>
                        <textarea
                            value={data.district.whatsapp_thankyou_template || ""}
                            onChange={(e) => setData({ ...data, district: { ...data.district, whatsapp_thankyou_template: e.target.value } })}
                            rows={3}
                            className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                            placeholder="Thanks {name}! We've received your directory entry for {year}. Edit later at: {link}"
                            data-testid="settings-whatsapp-thankyou"
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">Sent automatically the moment a member taps Submit on their form (WhatsApp + email if configured).</p>
                    </div>
                </div>
                <Button type="submit" disabled={savingD} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="settings-district-save">
                    {savingD ? "Saving…" : "Save district"}
                </Button>
            </form>

            <form onSubmit={saveYear} className="rounded-xl border bg-white p-6 space-y-4">
                <h3 className="rotary-heading text-lg text-rotary-ink">Year details · {data.year.year}</h3>
                <div className="flex items-center gap-4">
                    <div className="grid h-20 w-20 place-items-center rounded-full bg-muted overflow-hidden border">
                        {data.year.governor_photo_url ? (
                            <img src={imgUrl(data.year.governor_photo_url)} alt="Governor" className="h-full w-full object-cover" />
                        ) : (
                            <span className="text-xs text-muted-foreground">No photo</span>
                        )}
                    </div>
                    <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadGov(e.target.files[0])} data-testid="governor-upload-input" />
                        <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-muted"><Upload className="h-3.5 w-3.5" /> Upload governor photo</span>
                    </label>
                </div>
                <div><Label className="label-eyebrow">Governor name</Label>
                    <Input value={data.year.governor_name} onChange={(e) => setData({ ...data, year: { ...data.year, governor_name: e.target.value } })} data-testid="settings-governor-name" /></div>
                <div><Label className="label-eyebrow">Theme line</Label>
                    <Input value={data.year.theme_line} onChange={(e) => setData({ ...data, year: { ...data.year, theme_line: e.target.value } })} placeholder="Serve to Change Lives" /></div>
                <div><Label className="label-eyebrow">Governor message</Label>
                    <textarea value={data.year.governor_message} onChange={(e) => setData({ ...data, year: { ...data.year, governor_message: e.target.value } })} rows={4} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="A note from the Governor for the directory foreword…" /></div>
                <div>
                    <Label className="label-eyebrow">Data source URL (Google Sheet)</Label>
                    <Input
                        type="url"
                        value={data.year.sheet_url || ""}
                        onChange={(e) => setData({ ...data, year: { ...data.year, sheet_url: e.target.value } })}
                        placeholder="https://docs.google.com/spreadsheets/d/…"
                        data-testid="settings-year-sheet-url"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                        Paste the full Google Sheet URL that holds this year&apos;s roster. Make sure the sheet is shared with the service account (Editor access) so Sync can read and write back to it.
                    </p>
                </div>
                <Button type="submit" disabled={savingY} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="settings-year-save">
                    {savingY ? "Saving…" : "Save year"}
                </Button>
            </form>
        </div>
    );
}

function positionRank(pos) {
    const p = String(pos || "").toLowerCase();
    if (!p) return 0;
    if (p.includes("district governor") && p.includes("nominee")) return 90;
    if (p.includes("district governor") && p.includes("elect")) return 95;
    if (p.includes("past district governor") || p.includes("pdg") || p.includes("ipdg")) return 85;
    if (p.includes("district governor") || /\bdg\b/.test(p)) return 100;
    if (p.includes("vice governor")) return 78;
    if (p.includes("assistant governor") || /\bag\b/.test(p)) return 75;
    if (p.includes("zone governor") || p.includes("zone chair") || p.includes("committee chair")) return 72;
    if (p.includes("president") && p.includes("elect")) return 58;
    if (p.includes("past president") || p.includes("ipp")) return 55;
    if (p.includes("president")) return 60;
    if (p.includes("secretary")) return 50;
    if (p.includes("treasurer")) return 48;
    if (p.includes("director") || p.includes("board")) return 45;
    if (p.includes("sergeant")) return 40;
    if (p.includes("chair")) return 35;
    if (p.includes("member") || p.includes("rotarian")) return 10;
    return 20;
}

// Positions that count as "Club leadership" — get a small badge in list views.
function isClubLeadership(pos) {
    const p = String(pos || "").toLowerCase();
    if (!p) return false;
    return p.includes("president") || p.includes("secretary");
}

// Return the FIRST club-leadership position label found across a member's
// assignments (e.g. "President", "Secretary", "President Elect"). Falls
// back to null when none is present.
function firstLeadershipPosition(assignments) {
    for (const a of assignments || []) {
        for (const p of a.positions || []) {
            if (isClubLeadership(p)) return String(p).trim();
        }
    }
    return null;
}

function hasClubLeadership(assignments) {
    return (assignments || []).some((a) => (a.positions || []).some(isClubLeadership));
}


/**
 * PositionsPanel — CRUD for the sequence-ranked `positions` collection.
 * Feeds the "Generate sheet" sort priority: rows with a lower sequence
 * number for their Position value appear earlier inside each committee
 * bucket. Admin-only edits; hidden-but-visible read state when !canWrite.
 */
function PositionsPanel({ yid, canWrite = true }) {
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState("");
    const [newSeq, setNewSeq] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/scoped/district-years/${yid}/positions`);
            setPositions(data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not load positions"); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (yid) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [yid]);

    const add = async () => {
        const name = newName.trim();
        const seq = parseInt(newSeq, 10);
        if (!name) { toast.error("Position name is required"); return; }
        if (!Number.isFinite(seq) || seq < 0) { toast.error("Sequence must be a positive whole number"); return; }
        setSaving(true);
        try {
            await api.post(`/scoped/district-years/${yid}/positions`, { name, sequence: seq });
            setNewName(""); setNewSeq("");
            toast.success(`Position "${name}" saved with sequence ${seq}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const editSeq = async (p) => {
        const raw = window.prompt(`Sequence for "${p.name}" (lower = earlier):`, String(p.sequence));
        if (raw === null) return;
        const next = parseInt(raw.trim(), 10);
        if (!Number.isFinite(next) || next < 0) { toast.error("Enter a positive whole number"); return; }
        if (next === p.sequence) return;
        try {
            await api.patch(`/scoped/positions/${p.id}`, { name: p.name, sequence: next });
            toast.success(`Sequence for "${p.name}" set to ${next}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };

    const del = async (p) => {
        if (!window.confirm(`Delete position "${p.name}"? Rows already assigned this position keep their string value; only the sequence link is removed.`)) return;
        try {
            await api.delete(`/scoped/positions/${p.id}`);
            toast.success("Position deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="mt-6 rounded-xl border bg-white overflow-hidden" data-testid="positions-panel">
            <div className="border-b px-4 py-3 flex items-center gap-2">
                <span className="rotary-heading text-base flex-1">Positions</span>
                <span className="text-xs text-muted-foreground">
                    {loading ? "…" : `${positions.length} position${positions.length !== 1 ? "s" : ""}`}
                </span>
            </div>
            {canWrite && (
                <div className="border-b px-4 py-3 flex gap-2 flex-wrap">
                    <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Position name (e.g. Chairman)"
                        onKeyDown={(e) => e.key === "Enter" && add()}
                        className="flex-1 min-w-[180px]"
                        data-testid="position-new-name"
                    />
                    <Input
                        value={newSeq}
                        onChange={(e) => setNewSeq(e.target.value.replace(/\D+/g, ""))}
                        placeholder="Seq"
                        inputMode="numeric"
                        className="w-24"
                        data-testid="position-new-seq"
                    />
                    <Button
                        onClick={add}
                        disabled={saving || !newName.trim() || !newSeq.trim()}
                        className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                        data-testid="position-add-btn"
                    >
                        <Plus className="mr-1.5 h-4 w-4" />Add
                    </Button>
                </div>
            )}
            <div className="divide-y">
                {positions.length === 0 && !loading && (
                    <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                        No positions yet. Add one above — lower sequence numbers appear earlier in the Generate Sheet output.
                    </div>
                )}
                {positions.map((p) => (
                    <div key={p.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`position-row-${p.name}`}>
                        <span className="rotary-chip text-[10px] bg-rotary-blue/10 border-rotary-blue/30 text-rotary-blue min-w-[42px] justify-center">Seq {p.sequence}</span>
                        <span className="flex-1 truncate">{p.name}</span>
                        {canWrite && (
                            <>
                                <Button variant="outline" size="sm" onClick={() => editSeq(p)} className="rounded-full text-xs h-7" data-testid={`position-edit-${p.name}`}>
                                    Edit seq
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => del(p)} className="rounded-full text-xs h-7 text-rotary-cranberry hover:bg-rotary-cranberry/10" data-testid={`position-del-${p.name}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}


function CommitteeTree({ committees, members, onAddChild, onRename, onDelete, onEditSeq, onOpenMember, canWrite = true }) {
    // Build parent -> children map
    const childrenMap = {};
    committees.forEach((c) => {
        const key = c.parent_id || "__root__";
        (childrenMap[key] = childrenMap[key] || []).push(c);
    });
    // Stable sort by display_order then name at each level
    Object.values(childrenMap).forEach((arr) => {
        arr.sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name));
    });
    const roots = childrenMap["__root__"] || [];

    // Members assigned directly to a committee, sorted by seniority (highest position first) then name.
    const membersAt = (cId) => {
        const rows = members.filter((m) => m.year_role.committee_assignments.some((a) => a.committee_id === cId));
        // For each member, find their max positionRank at THIS committee only
        const rankAt = (m) => {
            const assigns = m.year_role.committee_assignments.filter((a) => a.committee_id === cId);
            let best = 0;
            assigns.forEach((a) => {
                (a.positions || []).forEach((p) => { best = Math.max(best, positionRank(p)); });
            });
            return best;
        };
        return [...rows].sort((a, b) => rankAt(b) - rankAt(a) || a.name.localeCompare(b.name));
    };

    // Recursive count including descendants (unique members)
    const subtreeMemberCount = (cId) => {
        const seen = new Set();
        const stack = [cId];
        while (stack.length) {
            const id = stack.pop();
            members.forEach((m) => {
                if (m.year_role.committee_assignments.some((a) => a.committee_id === id)) {
                    seen.add(m.year_role.id);
                }
            });
            (childrenMap[id] || []).forEach((k) => stack.push(k.id));
        }
        return seen.size;
    };

    // Expanded roots by default; children collapsed. Persist per year in localStorage.
    const storageKey = "yearview:committee-open";
    const [openIds, setOpenIds] = useState(() => {
        if (typeof window === "undefined") return new Set();
        try {
            // NB: `openIds` is UI-only state — the set of expanded committee
            // node IDs. Never contains credentials or PII. Storing this in
            // localStorage is intentional (survives reload; user preference).
            const saved = JSON.parse(window.localStorage.getItem(storageKey) || "null");
            if (Array.isArray(saved)) return new Set(saved);
        } catch {
            // localStorage may be blocked (private mode, quota, etc.) — fall through to defaults.
        }
        // Default: only roots expanded
        return new Set(roots.map((r) => r.id));
    });
    useEffect(() => {
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(Array.from(openIds)));
            } catch (e) {
                // Storage may be full or private mode. UI still works — the
                // tree just resets on the next reload. Log so we notice if
                // this becomes chronic in prod (e.g., admins in privacy mode).
                console.warn("committee-tree localStorage persist failed:", e);
            }
        }
    }, [openIds]);

    const toggle = (id) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    if (roots.length === 0) return <p className="p-6 text-sm text-muted-foreground">No committees yet.</p>;

    const renderNode = (c, depth = 0, isLast = false) => {
        const kids = childrenMap[c.id] || [];
        const directMembers = membersAt(c.id);
        const totalCount = subtreeMemberCount(c.id);
        const directCount = directMembers.length;
        const hasKids = kids.length > 0;
        const isOpen = openIds.has(c.id);
        const canAddChild = c.depth < 5;

        return (
            <li key={c.id} className="relative" data-testid={`committee-node-${c.name}`}>
                {/* Row */}
                <div
                    className={`group flex items-center gap-2 py-2 pl-3 pr-2 rounded-lg hover:bg-muted/40 transition-colors ${depth === 0 ? "" : "text-sm"}`}
                    data-testid={`committee-row-${c.name}`}
                >
                    {/* Expand toggle */}
                    <button
                        type="button"
                        onClick={() => toggle(c.id)}
                        className={`grid place-items-center h-6 w-6 rounded-md text-muted-foreground hover:text-rotary-blue hover:bg-white shrink-0 ${hasKids ? "" : "invisible"}`}
                        aria-label={isOpen ? "Collapse" : "Expand"}
                        data-testid={`committee-toggle-${c.name}`}
                    >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>

                    {/* Node dot */}
                    <span
                        className={`grid place-items-center rounded-full ${depth === 0 ? "h-7 w-7 bg-rotary-blue text-white" : depth === 1 ? "h-6 w-6 bg-rotary-blue/10 text-rotary-blue border border-rotary-blue/20" : "h-5 w-5 bg-muted text-muted-foreground border"} shrink-0`}
                    >
                        <Layers className={depth === 0 ? "h-3.5 w-3.5" : "h-3 w-3"} />
                    </span>

                    {/* Name + counts */}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={depth === 0 ? "font-semibold text-rotary-ink" : depth === 1 ? "font-medium text-rotary-ink" : "text-rotary-ink"}>{c.name}</span>
                            <span
                                className={`rotary-chip text-[10px] ${totalCount > 0 ? "bg-rotary-blue/5 border-rotary-blue/20 text-rotary-blue" : "bg-muted text-muted-foreground"}`}
                                data-testid={`committee-member-count-${c.name}`}
                                title={`${totalCount} member${totalCount !== 1 ? "s" : ""} (including sub-committees)`}
                            >
                                <Users className="h-3 w-3 mr-1" />
                                {totalCount}
                            </span>
                            {canAddChild && (
                                <span
                                    className={`rotary-chip text-[10px] ${hasKids ? "bg-rotary-gold/10 border-rotary-gold/40 text-rotary-ink" : "bg-muted text-muted-foreground"}`}
                                    data-testid={`committee-child-count-${c.name}`}
                                    title={`${kids.length} sub-committee${kids.length !== 1 ? "s" : ""}`}
                                >
                                    <Layers className="h-3 w-3 mr-1" />
                                    {kids.length} sub
                                </span>
                            )}
                            {/* iter 71 — sequence for Generate Sheet. Click to edit. */}
                            {canWrite && onEditSeq && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onEditSeq(c); }}
                                    className={`rotary-chip text-[10px] cursor-pointer hover:bg-rotary-blue hover:text-white ${c.display_order ? "bg-rotary-blue/10 border-rotary-blue/30 text-rotary-blue" : "bg-muted text-muted-foreground border-dashed"}`}
                                    data-testid={`committee-seq-${c.name}`}
                                    title="Click to set the export sequence for this committee (lower = earlier)"
                                >
                                    Seq {c.display_order ?? "—"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        {canWrite && canAddChild && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onAddChild(c)}
                                title="Add sub-committee"
                                data-testid={`committee-addsub-${c.name}`}
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </Button>
                        )}
                        {canWrite && (
                            <Button
                                variant="ghost" size="sm"
                                onClick={() => onRename(c)}
                                title="Rename"
                                data-testid={`committee-edit-${c.name}`}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                        )}
                        {canWrite && (
                            <Button
                                variant="ghost" size="sm"
                                onClick={() => onDelete(c)}
                                title="Delete"
                                data-testid={`committee-delete-${c.name}`}
                            >
                                <Trash2 className="h-3.5 w-3.5 text-rotary-cranberry" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Expanded content: children only (member list intentionally omitted for privacy) */}
                {isOpen && hasKids && (
                    <div className="relative ml-3">
                        {/* Vertical guide line */}
                        <span className="absolute left-3 top-0 bottom-2 w-px bg-border" aria-hidden="true" />
                        <ul className="list-none pl-6 space-y-0.5">
                            {kids.map((k, i) => renderNode(k, depth + 1, i === kids.length - 1))}
                        </ul>
                    </div>
                )}
            </li>
        );
    };

    return (
        <ul className="list-none p-3 space-y-0.5" data-testid="committee-tree">
            {roots.map((r, i) => renderNode(r, 0, i === roots.length - 1))}
        </ul>
    );
}

function EditAssignmentsDialog({ member, onOpenChange, onSaved, committees }) {
    const [assignments, setAssignments] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (member) setAssignments(JSON.parse(JSON.stringify(member.year_role.committee_assignments || []))); }, [member]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/scoped/year-roles/${member.year_role.id}`, { committee_assignments: assignments.filter((a) => a.committee_id) });
            toast.success("Updated");
            onSaved?.();
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!member} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl">Edit assignments</DialogTitle>
                    <p className="text-sm text-muted-foreground">{member?.name} · {member?.phone}</p>
                </DialogHeader>
                <AssignmentEditor value={assignments} onChange={setAssignments} committees={committees} />
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="edit-assign-save">{saving ? "Saving…" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AssignmentEditor({ value, onChange, committees }) {
    // Stable client-side keys per row so React doesn't lose focus/state when a middle row is removed.
    const keysRef = useRef([]);
    // Extend the ref to match `value.length` on every render (add) and shrink on delete.
    while (keysRef.current.length < value.length) keysRef.current.push(`row-${Math.random().toString(36).slice(2, 10)}`);
    if (keysRef.current.length > value.length) keysRef.current.length = value.length;
    const rowKey = (i) => keysRef.current[i];

    const add = () => onChange([...value, { committee_id: "", positions: ["Member"] }]);
    const upd = (i, patch) => onChange(value.map((a, j) => j === i ? { ...a, ...patch } : a));
    const del = (i) => {
        keysRef.current.splice(i, 1);
        onChange(value.filter((_, j) => j !== i));
    };

    // Build hierarchical label: "Parent / Child / Grandchild"
    const byId = Object.fromEntries(committees.map((c) => [c.id, c]));
    const pathFor = (c) => {
        const parts = [c.name];
        let cur = c;
        while (cur.parent_id && byId[cur.parent_id]) {
            cur = byId[cur.parent_id];
            parts.unshift(cur.name);
        }
        return parts.join(" / ");
    };
    // Sort so parents render before children
    const sorted = [...committees].sort((a, b) => pathFor(a).localeCompare(pathFor(b)));

    return (
        <div className="space-y-2">
            <Label className="label-eyebrow">Committee assignments</Label>
            {value.length === 0 && <p className="text-xs text-muted-foreground italic">No committees assigned yet.</p>}
            {value.map((a, i) => (
                <div key={rowKey(i)} className="flex gap-2 items-center" data-testid={`assign-row-${i}`}>
                    <select value={a.committee_id} onChange={(e) => upd(i, { committee_id: e.target.value })} className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm">
                        <option value="">Select committee…</option>
                        {sorted.map((c) => (<option key={c.id} value={c.id}>{pathFor(c)}</option>))}
                    </select>
                    <Input value={(a.positions || []).join(", ")} onChange={(e) => upd(i, { positions: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Chair, Member" className="flex-1" data-testid={`assign-positions-${i}`} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => del(i)}><Trash2 className="h-4 w-4 text-rotary-cranberry" /></Button>
                </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={add} className="rounded-full mt-1" data-testid="assign-add-row"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add committee</Button>
        </div>
    );
}
